import React from 'react';
import type { AaiRequest, AaiSkill } from '../../types/aai';
import { Action, Chip, ListRow, RouteMark, StatusChip } from '../../components/aa';
import { aaiApi } from '../../services/aaiApi';
import { useOpenWorkspace } from '../../hooks/useOpenWorkspace';
import { nextFor, stateFor } from '../partner/work';
import { relativeTime } from '../partner/format';

/*
 * The rows the Owner's lists share.
 *
 * Every piece of company work is either a request (a Project, or a request on
 * its way to one) or a published company skill (a Quick Win). Home, Portfolio
 * and Projects all list them, so the row lives here once: the same words for a
 * state, the same owner rule, the same "what happens next" line.
 */

// Whether the request waits on the reader, decided by the server from the same
// routing the decision uses. The status rule is the fallback for a row without it.
export const needsOwner = (r: AaiRequest): boolean => {
  if (typeof r.needs_you === 'boolean') return r.needs_you;
  return r.status === 'dept_approved' || (r.source === 'orbit_problem' && r.status === 'pending');
};

export const isReadyToUse = (r: AaiRequest): boolean => Boolean(r.tool_skill_id);

// A row is a summary. The full statement opens in the record.
export const brief = (text: string | null | undefined): string | undefined => {
  if (!text) return undefined;
  if (text.length <= 180) return text;
  return `${text.slice(0, 177).trimEnd()}…`;
};

export const askedBy = (request: AaiRequest): string => request.requester_name || request.requester_email || 'Unknown';

// A request's title is its statement, and an Orbit card's title is the first
// 72 characters of it, so a description would only repeat the title. The row
// marks the cut and the drawer carries the full text.
export const titleIsCutOf = (title: string, text: string | null | undefined): boolean => Boolean(title && (text || '').startsWith(title));

export const rowTitle = (title: string, text: string | null | undefined): string => {
  if (!titleIsCutOf(title, text) || text === title) return title;
  return `${title.trimEnd()}…`;
};

export const rowDesc = (title: string, text: string | null | undefined): string | undefined => {
  if (titleIsCutOf(title, text)) return undefined;
  return brief(text);
};

/* Every list row has the same shape: one line of title, a quiet line of
   chips and who/when under it, and one fixed-width column for what happens
   next, so the column lines up down every list and every tab. */
export const NEXT_COLUMN = [300];

export const RowFoot: React.FC<{ chips: React.ReactNode; text?: string }> = ({ chips, text }) => (
  <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
    {chips}
    {text && <span className="text-[12px] leading-[18px] text-ink-dim">{text}</span>}
  </span>
);

export const NextCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="line-clamp-2">{children}</span>
);

export const WorkRow: React.FC<{ request: AaiRequest; onOpen: (id: string) => void; className?: string }> = ({ request, onOpen, className }) => {
  const ready = isReadyToUse(request);
  const kind = ready ? 'quick_win' : 'project';
  const status = ready ? 'ready_short' : stateFor(request);
  return (
    <ListRow
      dense
      mark={<RouteMark kind={kind} size="lg" />}
      title={request.title || rowTitle(request.title, request.detail)}
      foot={(
        <RowFoot
          chips={(
            <>
              <StatusChip status={status} />
              {needsOwner(request) && <Chip tone="warn" dot>Needs you</Chip>}
            </>
          )}
          text={`${askedBy(request)} · ${relativeTime(request.created_at)}`}
        />
      )}
      meta={[{ label: 'Next', value: <NextCell>{nextFor(request)}</NextCell> }]}
      metaWidth={NEXT_COLUMN}
      onClick={() => onOpen(request.id)}
      arrow
      className={className}
    />
  );
};

// The same rule the record's Workspace card uses: a request is still being
// scoped until its brief becomes a build, and a Quick Win is never scoped.
export const isScoping = (r: AaiRequest): boolean => Boolean(r.problem_id) && !r.builder_project_id && r.route !== 'quick_win';

/* A Clarity row with the door to carry on scoping beside it. The row itself
   is a button, so the door sits next to it, in a slot every row keeps so the
   columns line up whether or not a row has one. */
export const ClarityRow: React.FC<{ request: AaiRequest; workspaceId: string | null; onOpen: (id: string) => void }> = ({ request, workspaceId, onOpen }) => {
  const { opening, open } = useOpenWorkspace();
  const carryOn = () => open(workspaceId, async () => (await aaiApi.scopeInWorkspace(request.id)).data.path);
  return (
    <div className="flex flex-col transition-colors hover:bg-canvas md:flex-row md:items-center">
      <div className="min-w-0 flex-1">
        <WorkRow request={request} onOpen={onOpen} className="hover:!bg-transparent" />
      </div>
      <div className={`shrink-0 px-5 pb-4 md:w-[160px] md:justify-end md:px-0 md:pb-0 md:pr-6 ${isScoping(request) ? 'flex' : 'hidden md:flex'}`}>
        {isScoping(request) && (
          <Action size="sm" variant="quiet" arrow onClick={carryOn} disabled={opening} className="h-[28px]">Continue scoping</Action>
        )}
      </div>
    </div>
  );
};

const ACCESS_LABEL: Record<string, string> = {
  open: 'Everyone in the company',
  request: 'On request',
  restricted: 'Named people',
};

export const audienceOf = (skill: AaiSkill): string => ACCESS_LABEL[skill.access_level] || skill.access_level;

export const CapabilityRow: React.FC<{ skill: AaiSkill; onOpen: (skill: AaiSkill) => void }> = ({ skill, onOpen }) => (
  <ListRow
    dense
    mark={<RouteMark kind="quick_win" size="lg" />}
    title={skill.name}
    foot={(
      <RowFoot
        chips={(
          <>
            <StatusChip status="ready_short" />
            {skill.shape === 'plugin' && <Chip tone="private">Plugin</Chip>}
          </>
        )}
        text={skill.source === 'provider' ? 'Every company' : audienceOf(skill)}
      />
    )}
    meta={[{ label: 'Owner', value: <NextCell>{skill.source === 'provider' ? 'Agents Anywhere' : (skill.created_by || 'Not recorded')}</NextCell> }]}
    metaWidth={NEXT_COLUMN}
    onClick={() => onOpen(skill)}
    arrow
  />
);
